import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiError, formatINR } from "@/lib/api";
import { LandingNav, LandingFooter } from "@/components/landing/LandingSections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SlotPicker from "@/components/SlotPicker";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Copy, Search } from "lucide-react";

const fmt = (s) => new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const phoneDigits = (p) => (p || "").replace(/\D/g, "");

function Success({ booking, onReset }) {
  const copy = () => navigator.clipboard?.writeText(booking.reference_id).then(() => toast.success("Reference ID copied"));
  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-8 text-center animate-fade-up" data-testid="booking-success">
      <CheckCircle2 className="h-14 w-14 text-teal-600 mx-auto mb-4" />
      <h2 className="font-head text-2xl font-bold text-slate-900">Appointment Requested!</h2>
      <p className="text-slate-500 mt-2">Save this reference ID to track your appointment.</p>
      <button onClick={copy} className="mt-5 inline-flex items-center gap-3 bg-[#0F172A] text-white rounded-2xl px-6 py-4 font-mono text-2xl tracking-widest hover:bg-slate-800" data-testid="booking-reference-id">
        {booking.reference_id} <Copy className="h-5 w-5 text-teal-400" />
      </button>
      <div className="mt-6 grid sm:grid-cols-3 gap-3 text-left text-sm">
        <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-400">Doctor</div><div className="font-semibold">{booking.doctor_name}</div></div>
        <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-400">Date & Time</div><div className="font-semibold">{fmt(booking.appointment_date)}</div></div>
        <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-400">Consultation Fee</div><div className="font-semibold font-mono text-teal-700">{formatINR(booking.amount)}</div></div>
      </div>
      <p className="text-xs text-slate-400 mt-4">Status: <span className="capitalize font-medium text-amber-700">{booking.status}</span> — our front desk will confirm shortly.</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to={`/track?ref=${booking.reference_id}`}><Button className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="go-track-button"><Search className="h-4 w-4 mr-2" /> Track Appointment</Button></Link>
        <Button variant="outline" onClick={onReset} data-testid="book-another-button">Book Another</Button>
      </div>
    </div>
  );
}

export default function BookOnline() {
  const [params] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState({ doctor_id: params.get("doctor") || "", patient_name: "", patient_age: "", phone: "", date: "", time: "", disease_name: "" });
  const [sending, setSending] = useState(false);
  const [booking, setBooking] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const doctor = doctors.find((d) => d.id === form.doctor_id);

  useEffect(() => { api.get("/public/doctors").then((r) => setDoctors(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!form.doctor_id) return toast.error("Please select a doctor");
    if (!form.patient_name.trim()) return toast.error("Please enter your name");
    if (!form.patient_age) return toast.error("Please enter your age");
    if (phoneDigits(form.phone).length < 10) return toast.error("A valid 10-digit mobile number is required to book");
    if (!form.date || !form.time) return toast.error("Please choose a date and an available time slot");
    setSending(true);
    try {
      const { date, time, ...rest } = form;
      const r = await api.post("/public/book", { ...rest, appointment_date: `${date}T${time}`, patient_age: parseInt(form.patient_age, 10) });
      setBooking(r.data);
    } catch (e) { toast.error(apiError(e, "Could not book. Please try again.")); } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <LandingNav />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        {booking ? <Success booking={booking} onReset={() => { setBooking(null); setForm((f) => ({ ...f, patient_name: "", patient_age: "", date: "", time: "", disease_name: "" })); }} /> : (
          <div className="animate-fade-up">
            <div className="text-xs font-medium uppercase tracking-wider text-teal-600 mb-3">Online Booking</div>
            <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Book an appointment</h1>
            <p className="mt-3 text-slate-500">No login needed. A valid mobile number is required — you'll get a reference ID to track your visit.</p>

            <div className="mt-8 bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 grid sm:grid-cols-2 gap-5" data-testid="public-booking-form">
              <div className="space-y-2 sm:col-span-2">
                <Label>Doctor</Label>
                <Select value={form.doctor_id} onValueChange={(v) => set("doctor_id", v)}>
                  <SelectTrigger data-testid="public-doctor-select" className="h-12"><SelectValue placeholder="Choose a doctor" /></SelectTrigger>
                  <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} — {d.specialty} ({formatINR(d.fee)})</SelectItem>)}</SelectContent>
                </Select>
                {doctor && <p className="text-xs text-slate-500">{doctor.available_days}{doctor.available_hours ? ` • ${doctor.available_hours}` : ""}{doctor.location ? ` • ${doctor.location}` : ""}</p>}
              </div>
              <div className="space-y-2"><Label>Your Name</Label><Input data-testid="public-name-input" className="h-12" value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} placeholder="Full name" /></div>
              <div className="space-y-2"><Label>Age</Label><Input data-testid="public-age-input" className="h-12" type="number" min="0" value={form.patient_age} onChange={(e) => set("patient_age", e.target.value)} placeholder="Age" /></div>
              <div className="space-y-2"><Label>Mobile Number <span className="text-rose-600">*</span></Label><Input data-testid="public-phone-input" className="h-12" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit mobile number" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>Concern (optional)</Label><Input data-testid="public-concern-input" className="h-12" value={form.disease_name} onChange={(e) => set("disease_name", e.target.value)} placeholder="e.g. Fever, skin rash, back pain" /></div>
              <div className="sm:col-span-2">
                <SlotPicker doctorId={form.doctor_id} date={form.date} onDateChange={(v) => set("date", v)} value={form.time} onChange={(v) => set("time", v)} />
              </div>
              <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-slate-100">
                <div className="text-sm text-slate-500">Consultation fee <span className="font-mono font-bold text-lg text-teal-700 ml-2" data-testid="public-fee-display">{doctor ? formatINR(doctor.fee) : "—"}</span><span className="block text-xs">Payable at the clinic</span></div>
                <Button data-testid="public-book-submit" onClick={submit} disabled={sending} className="h-12 px-8 bg-teal-600 hover:bg-teal-700 text-white text-base">
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Confirm Booking"}
                </Button>
              </div>
            </div>
            <p className="mt-5 text-sm text-slate-500">Already booked? <Link to="/track" className="text-teal-700 font-medium hover:underline" data-testid="track-link">Track your appointment</Link></p>
          </div>
        )}
      </main>
      <LandingFooter />
    </div>
  );
}
